import i18n from '@/i18n';
import { getKeycloak, getToken } from '@nxtpeople/nxt-vue-keycloak';
import axios, { AxiosResponse } from 'axios';
import router from '../router';
import store from '../store';

const INACTIVE_ACCESS_TOKEN_MESSAGE = 'The access token is not active';

interface AuthAxiosRequestConfig {
   _retryInactiveToken?: boolean;
}

async function forceRefreshAccessToken(): Promise<boolean> {
   const keycloak = getKeycloak();
   if (!keycloak) {
      return false;
   }

   try {
      await keycloak.updateToken(-1);
      return true;
   } catch {
      return false;
   }
}

function isInactiveAccessToken401(error: any): boolean {
   return error.response?.status === 401 && error.response?.data?.message === INACTIVE_ACCESS_TOKEN_MESSAGE;
}

function redirectToUnauthorizedLogout(error: any) {
   const customMessage = error.response.data?.message || i18n.global.t('errors.account-not-found');
   router.push({
      path: '/error',
      query: {
         error_code: '401',
         error_title: customMessage,
         error_message: `${store.state.isDev ? `${JSON.stringify(error)}` : i18n.global.t('generic.account-not-found-description')}`,
         error_action: 'logout',
      },
   });
}

async function retryWithHttp(requestConfig: AuthAxiosRequestConfig) {
   // Lazy import avoids circular dependency with axiosHelper (which registers this interceptor).
   const { http } = await import('../helpers/axiosHelper');
   return http(requestConfig);
}

const authRequestInterceptor = async function (config: any) {
   const token = await getToken();

   config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
      Accept: `application/json`,
   };

   return config;
};

const authResponseSuccessInterceptors = async function (response: AxiosResponse) {
   try {
      const backendVersion = response.headers['x-api-version'];
      checkBackendVersion(backendVersion);
   } catch {
      console.warn('Error while getting backend version');
   }
   return response;
};

const authResponseErrorInterceptors = async function (error: any) {
   if (axios.isCancel(error)) return await Promise.reject(error);

   if (!error?.response) {
      return await Promise.reject(error);
   }

   const { status } = error.response;

   if (status === 401) {
      const requestConfig = error.config as AuthAxiosRequestConfig | undefined;

      if (isInactiveAccessToken401(error) && requestConfig && !requestConfig._retryInactiveToken) {
         const refreshed = await forceRefreshAccessToken();
         if (refreshed) {
            requestConfig._retryInactiveToken = true;
            return retryWithHttp(requestConfig);
         }
      }

      redirectToUnauthorizedLogout(error);
   }

   return await Promise.reject(error);
};

export { authRequestInterceptor, authResponseErrorInterceptors, authResponseSuccessInterceptors };
